// src/styles/CommonStyles.js
import styled, { css } from 'styled-components';

export const Card = styled.div`
    background-color: ${({ theme }) => theme.colors.white};
    padding: ${({ theme }) => theme.spacing.lg};
    border-radius: ${({ theme }) => theme.borderRadius};
    box-shadow: ${({ theme }) => theme.shadows.md};
    border: 1px solid ${({ theme }) => theme.colors.borderColor};

    @media (max-width: 768px) {
        padding: 0.5rem;
    }
`;

export const StyledTextarea = styled.textarea`
    width: 100%;
    padding: ${({ theme }) => theme.spacing.md};
    border: 1px solid ${({ theme }) => theme.colors.borderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    font-family: ${({ theme }) => theme.fonts.main};
    font-size: 1rem;
    min-height: 150px;
    resize: vertical;
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.colors.primary};
        box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryLightest};
    }
`;

export const StyledButton = styled.button`
    display: inline-flex; // Aby ikony i tekst były poprawnie wyrównane
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing.sm}; // Odstęp między ikoną a tekstem
    font-weight: 600;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
    border-radius: ${({ theme }) => theme.buttonBorderRadius}; // Użyj okrągłego border-radius dla przycisków
    box-shadow: ${({ theme }) => theme.shadows.sm};
    transition: background-color 0.2s, transform 0.1s, box-shadow 0.2s, color 0.2s, border-color 0.2s;
    cursor: pointer;
    border: 2px solid transparent; // Domyślnie przezroczysty border dla utrzymania rozmiaru
    text-decoration: none; // Usuń podkreślenie, jeśli używane jako link

    // Domyślny (primary) styl
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};

    &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme.colors.primaryDark};
        box-shadow: ${({ theme }) => theme.shadows.md};
        transform: translateY(-1px);
    }
    &:active:not(:disabled) {
        transform: scale(0.97) translateY(0px);
    }
    &:disabled {
        background-color: ${({ theme }) => theme.colors.neutralMedium};
        color: ${({ theme }) => theme.colors.textDisabled};
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
        border-color: transparent;
    }

    // Wariant Secondary
    ${({ theme, $variant }) => $variant === 'secondary' && css`
        background-color: ${theme.colors.secondary};
        color: ${theme.colors.white};
        border-color: ${theme.colors.secondary};
        &:hover:not(:disabled) {
            background-color: ${theme.colors.secondaryDark};
            border-color: ${theme.colors.secondaryDark};
        }
    `}

    // Wariant Outline (np. dla mniej ważnych akcji lub alternatywnego wyglądu)
    ${({ theme, $variant }) => $variant === 'outline' && css`
        background-color: transparent;
        color: ${theme.colors.primary};
        border-color: ${theme.colors.primary};
        box-shadow: none;
        &:hover:not(:disabled) {
            background-color: ${theme.colors.primaryLightest};
            color: ${theme.colors.primaryDark};
            border-color: ${theme.colors.primaryDark};
        }
    `}

    // Wariant dla przycisków OAuth (jeśli chcesz je ostylować przez ten komponent)
    ${({ theme, $provider }) => $provider === 'google' && css`
        background-color: #DB4437;
        color: white;
        border-color: #DB4437;
        &:hover:not(:disabled) {
            background-color: #C33D2E;
            border-color: #C33D2E;
        }
    `}
    ${({ theme, $provider }) => $provider === 'facebook' && css`
        background-color: #4267B2;
        color: white;
        border-color: #4267B2;
        &:hover:not(:disabled) {
            background-color: #365899;
            border-color: #365899;
        }
    `}
`;

export const AlertBox = styled.div`
    background-color: ${({ theme, type }) =>
            type === 'success' ? theme.colors.secondaryLight + '40' : // Jasnozielone tło dla sukcesu
                    type === 'info' ? theme.colors.primaryLightest : // Jasnoniebieskie dla info
                            theme.colors.dangerBg}; // Domyślnie czerwone dla błędu
    border-left: 4px solid ${({ theme, type }) =>
            type === 'success' ? theme.colors.secondaryDark :
                    type === 'info' ? theme.colors.primary :
                            theme.colors.danger};
    color: ${({ theme, type }) =>
            type === 'success' ? theme.colors.secondaryDarker : // Ciemniejszy zielony tekst
                    type === 'info' ? theme.colors.primaryDark :
                            theme.colors.danger};
    padding: ${({ theme }) => theme.spacing.md};
    border-radius: ${({ theme }) => theme.borderRadius};
    box-shadow: ${({ theme }) => theme.shadows.sm};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    display: flex;
    align-items: flex-start; // lub center, jeśli ikona ma być na środku
    gap: ${({ theme }) => theme.spacing.md};

    svg { // Styl dla ikon w AlertBox
        color: ${({ theme, type }) =>
                type === 'success' ? theme.colors.secondaryDarker :
                        type === 'info' ? theme.colors.primaryDark :
                                theme.colors.danger};
        flex-shrink: 0;
        margin-top: 2px; // Drobne wyrównanie ikony z tekstem
    }
    p {
        margin: 0; // Usuń domyślne marginesy p wewnątrz AlertBox
        color: inherit; // Dziedzicz kolor tekstu z AlertBox
        font-size: 0.9rem;
        line-height: 1.5;
    }
    strong {
        font-weight: 600;
    }
`;

export const Logo = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.sm};
    font-size: 1.4rem; // Domyślny rozmiar, może być nadpisany
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primaryDark}; // Domyślny kolor
    text-decoration: none;

    svg {
        color: ${({ theme }) => theme.colors.primary};
        width: 28px; /* Domyślny rozmiar, można nadpisać w instancji */
        height: 28px;
    }

    span { // Dla tekstu obok logo
        color: inherit; // Dziedziczy kolor z diva Logo
    }
`;

export const Tag = styled.span`
    background-color: ${({ theme, color }) => color || theme.colors.secondaryLight + '40'};
    color: ${({ theme, textColor }) => textColor || theme.colors.secondaryDark};
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
    border-radius: ${({ theme }) => theme.borderRadius};
    font-size: 0.75rem;
    font-weight: 500;
    margin-right: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    display: inline-block;
`;

export const RatingContainer = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xs};
    color: ${({ theme }) => theme.colors.secondary};

    .rating-value {
        font-weight: 600;
        color: ${({ theme }) => theme.colors.textDark};
        margin-left: ${({ theme }) => theme.spacing.xs};
    }
`;